/**
 * Iter XX.267c — Native haptic feedback.
 *
 * XX.267b originally used `import("@capacitor/haptics")` (dynamic).
 * On iPhone WKWebView the lazy chunk load can silently fail because
 * webpack's default public path is wrong for the Capacitor
 * `capacitor://` origin, and even when the chunk arrives there is a
 * micro-task race where `ImpactStyle` (a TS `declare enum`) resolves
 * to `undefined` before `Haptics.impact({ style })` is called.
 * Result on real device: zero perceptible feedback.
 *
 * XX.267c switches to a **static** top-of-file import. The
 * `@capacitor/haptics` plugin ships its own web fallback so this is
 * safe on the browser, and the enum is available synchronously on
 * every call. `isNative()` still gates the actual invocation so web
 * users never trigger the (no-op) web fallback either.
 *
 * All calls remain:
 *   · fire-and-forget (never awaited)
 *   · guarded — never throws
 *   · 60ms-debounced so nested `<Link>` / parent handlers can't
 *     double-fire on the same tap
 *
 * Impact levels used across the app:
 *   · Light   — bottom-nav Home/Pulse/Memory/More, ordinary CTAs,
 *               conversation starter selection, "Pick up where you
 *               left off" resume tap
 *   · Medium  — central Gully bottom-nav action, cricket video upload
 *   · Success — Pro waitlist joined, Plus purchase completed
 *
 * Deliberately NOT used on: scrolling, passive renders, chat message
 * bubbles, text-input keystrokes, failed/disabled actions.
 */
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { isNative } from "./nativePlatform";

// Debounce so nested `<Link>`/parent handlers don't double-fire.
const DEBOUNCE_MS = 60;
let _lastFireAt = 0;

function _shouldFire() {
  const now = Date.now();
  if (now - _lastFireAt < DEBOUNCE_MS) return false;
  _lastFireAt = now;
  return true;
}

function _mapImpactStyle(style) {
  const s = String(style || "light").toLowerCase();
  if (s === "medium") return ImpactStyle.Medium;
  if (s === "heavy")  return ImpactStyle.Heavy;
  return ImpactStyle.Light;
}

/**
 * Fire an "impact" haptic. Style is `"light"` | `"medium"` | `"heavy"`.
 * Never throws; silently no-ops on web / unsupported.
 */
export function hapticImpact(style = "light") {
  try {
    if (!isNative || !isNative()) return;
    if (!_shouldFire()) return;
    const mapped = _mapImpactStyle(style);
    // Fire-and-forget. Any rejection is swallowed so a plugin-registration
    // hiccup on native never bubbles up as an unhandled rejection.
    Haptics.impact({ style: mapped }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}

/** Light impact — the workhorse tap confirmation. */
export function hapticLight()  { hapticImpact("light"); }

/** Medium impact — reserved for the central Gully tap and video upload. */
export function hapticMedium() { hapticImpact("medium"); }

/**
 * Success notification haptic. Used on Pro waitlist join success and
 * Plus purchase completion. Never fired on ordinary confirmations.
 */
export function hapticSuccess() {
  try {
    if (!isNative || !isNative()) return;
    if (!_shouldFire()) return;
    Haptics.notification({ type: NotificationType.Success }).catch(() => {});
  } catch { /* swallow */ }
}

/**
 * Error notification haptic. Used only for meaningful failures where
 * the user must act (e.g. upload rejected, purchase failed). Never
 * fired on passive errors or background failures.
 */
export function hapticError() {
  try {
    if (!isNative || !isNative()) return;
    if (!_shouldFire()) return;
    Haptics.notification({ type: NotificationType.Error }).catch(() => {});
  } catch { /* swallow */ }
}

/**
 * XX.267c dev/QA-only diagnostic. Fires one unmistakable Medium impact
 * and returns a structured result the founder can log from the physical
 * iPhone Safari inspector to prove the plugin bridge is alive.
 *
 * Exposed on `window.__gullyHapticDiag` when running natively so QA
 * can invoke it from the Web Inspector console without any UI change:
 *
 *     await window.__gullyHapticDiag()
 *
 * The mount point lives in `App.js` and is guarded by `isNative()` so
 * it never touches production web bundles.
 */
export async function hapticDiagnostic() {
  const result = {
    is_native: false,
    haptics_import_ok: true,
    impact_style_enum: null,
    invocation_attempted: false,
    invocation_error: null,
  };
  try {
    result.is_native = !!(isNative && isNative());
  } catch (e) {
    result.invocation_error = "isNative_threw: " + String(e?.message || e);
    return result;
  }
  try {
    result.impact_style_enum = String(ImpactStyle?.Medium ?? "undefined");
  } catch (e) {
    result.impact_style_enum = "enum_read_threw: " + String(e?.message || e);
  }
  if (!result.is_native) return result;
  try {
    result.invocation_attempted = true;
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (e) {
    result.invocation_error = String(e?.message || e);
  }
  return result;
}
